import React from 'react';
import * as Chart from 'recharts';

import { Switcher } from '@nearst/ui';

import * as Stats from '@components/StatsCard';
import NoData from '@components/Graphs/Placeholders/NoData';
import { TooltipContainer } from '@components/Graphs/Tooltip';
import { useStoredQuery } from '@services/insights';
import { useShop } from '@services/ShopProvider';
import { useIsLocalFeedPartner } from '@utils/HideForLocalFeedPartner';
import HideForLocalFeedPartner from '@utils/HideForLocalFeedPartner';
import findCommonPrefix from '@utils/findCommonPrefix';

import styles from './OrganicVisibilityContent.module.scss';

const Tooltip = ({ active, payload }) => {
	if (!active || !payload || !payload.length) {
		return null;
	}

	const { name, gbp_views, local_listings_views } = payload[0].payload;

	return (
		<TooltipContainer>
			<strong>{name}</strong>
			<p>GBP views: {gbp_views.toLocaleString()}</p>
			<HideForLocalFeedPartner>
				<p>Local listings views: {local_listings_views.toLocaleString()}</p>
			</HideForLocalFeedPartner>
		</TooltipContainer>
	);
};

const OrganicVisibilityContent = ({ options, shopIds }) => {
	const { data: shops } = useShop();
	const { data } = useStoredQuery('reports/local-impact', { shopIds }, { suspense: true });

	// Check if any of the shops are Local Feed Partners
	const isLocalFeedPartner = useIsLocalFeedPartner(shops?.[0]?.id);

	const totalViews = isLocalFeedPartner
		? data.reduce((acc, { gbp_views }) => acc + gbp_views, 0)
		: data.reduce((acc, { total_views }) => acc + total_views, 0);
	const localListingsViews = data.reduce((acc, { local_listings_views }) => acc + local_listings_views, 0);
	const averageGbpViews = data.reduce((acc, { gbp_views }) => acc + gbp_views, 0) / data.length;

	const hasLocalListings = data.filter((row) => row.local_listings_views > 0).map((row) => row.local_listings_views + row.gbp_views);
	const averageOrganicViews = hasLocalListings.reduce((acc, views) => acc + views, 0) / hasLocalListings.length;

	const visibilityIncrease = Math.round(((averageOrganicViews - averageGbpViews) / averageGbpViews) * 100);

	const chartData = data.map((item, index) => ({
		...item,
		index,
		name: shops.find((shop) => shop.id === item.shop_id)?.name
	}));

	const commonPrefix = findCommonPrefix(chartData.map((item) => item.name));

	if (!chartData.length || !averageGbpViews || !averageOrganicViews) {
		return <NoData text="No data available for the selected date range" />;
	}

	return (
		<>
			<Switcher space="2rem">
				<Stats.Figure>
					{totalViews.toLocaleString()}
					<Stats.FigureDescription>Total organic views</Stats.FigureDescription>
				</Stats.Figure>
				<HideForLocalFeedPartner>
					<Stats.Figure>
						{localListingsViews.toLocaleString()}
						<Stats.FigureDescription>Shoppers reached with local inventory</Stats.FigureDescription>
					</Stats.Figure>
				</HideForLocalFeedPartner>
				<Stats.Figure>
					{Math.round(averageGbpViews).toLocaleString()}
					<Stats.FigureDescription>Average GBP views</Stats.FigureDescription>
				</Stats.Figure>
				<HideForLocalFeedPartner>
					<Stats.Figure>
						{Math.round(averageOrganicViews).toLocaleString()}
						<Stats.FigureDescription>Average organic views with local inventory</Stats.FigureDescription>
					</Stats.Figure>
				</HideForLocalFeedPartner>
			</Switcher>

			<HideForLocalFeedPartner>
				{visibilityIncrease > 0 && (
					<p className={styles.description}>
						Adding local inventory increased your average organic views{' '}
						<strong>by {visibilityIncrease.toLocaleString()}%</strong>.
					</p>
				)}
			</HideForLocalFeedPartner>

			<div className={styles.chartContainer}>
				<Chart.ResponsiveContainer
					width={options.summaryView ? 650 : '100%'}
					maxHeight={options.summaryView ? 600 : undefined}
					minHeight={(options.summaryView ? 18 : 27) * chartData.length + 96}
				>
					<Chart.ComposedChart
						data={chartData}
						margin={{ top: 48, left: 0, right: 48, bottom: 64 }}
						layout="vertical"
						id="chart-container"
					>
						<Chart.XAxis type="number" hide />
						<Chart.YAxis
							dataKey="index"
							tickFormatter={(index) => chartData[index]?.name.replace(commonPrefix, '').trim()}
							type="number"
							width={240}
							stroke="translate"
							tickCount={chartData.length}
							interval={options.summaryView && chartData.length > 50 ? 1 : 0}
							domain={['dataMin', 'dataMax']}
						/>
						<Chart.Bar
							stackId={1}
							dataKey="gbp_views"
							radius={4}
							barSize={options.summaryView ? 13 : 20}
							fill="var(--grey)"
							style={{ stroke: 'white', strokeWidth: 2 }}
						/>
						{!isLocalFeedPartner && (
							<Chart.Bar
								stackId={1}
								dataKey="local_listings_views"
								radius={4}
								fill="var(--green)"
								style={{ stroke: 'white', strokeWidth: 2 }}
								label={
									options.dataLabels
										? {
												position: 'right',
												fill: 'var(--grey-darkest)',
												formatter: (v) => v.toLocaleString()
											}
										: undefined
								}
							/>
						)}
						{options.referenceLines && chartData.length > 1 && (
							<>
								<Chart.ReferenceLine
									stroke="var(--grey-darker)"
									ifOverflow="visible"
									segment={[
										{
											x: averageGbpViews,
											y: -0.5
										},
										{
											x: averageGbpViews,
											y: chartData.length - 0.5
										}
									]}
									label={{
										position: 'bottom',
										style: { fontWeight: 'bold' },
										value: 'Avg. GBP views',
										fill: 'var(--grey-darkest)'
									}}
								/>
								{!isLocalFeedPartner && (
									<Chart.ReferenceLine
										stroke="var(--grey-darker)"
										ifOverflow="visible"
										segment={[
											{
												x: averageOrganicViews,
												y: -0.5
											},
											{
												x: averageOrganicViews,
												y: chartData.length
											}
										]}
										label={{
											position: 'bottom',
											style: { fontWeight: 'bold' },
											value: 'Avg. with local inventory',
											fill: 'var(--grey-darkest)'
										}}
									/>
								)}
							</>
						)}
						<Chart.Tooltip cursor={false} content={Tooltip} />
					</Chart.ComposedChart>
				</Chart.ResponsiveContainer>
			</div>
		</>
	);
};

export default OrganicVisibilityContent;
